import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface ProductionMetrics {
  month: string;
  screens_produced: number;
  avg_labor_hours: number;
  total_labor_hours: number;
  avg_overhead_per_screen: number;
}

interface WorkstationMetrics {
  workstation_id: string;
  workstation_name: string;
  step_count: number;
  total_labor_hours: number;
  avg_time_per_screen: number;
  total_labor_cost: number;
}

interface ProductionChartProps {
  productionData: ProductionMetrics[] | null;
  workstationData: WorkstationMetrics[] | null;
  loading: boolean;
  view: 'monthly' | 'workstation';
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export function ProductionChart({ productionData, workstationData, loading, view }: ProductionChartProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {view === 'monthly' ? 'Monthly Production' : 'Time by Workstation'}
        </h3>
        <div className="h-80 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-blue-600" />
        </div>
      </div>
    );
  }

  const data = view === 'monthly' ? productionData : workstationData;

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {view === 'monthly' ? 'Monthly Production' : 'Time by Workstation'}
        </h3>
        <div className="h-80 flex items-center justify-center text-gray-500">
          No data available
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="font-medium text-gray-900 mb-2">{label}</p>
          {payload.map((item: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: item.color }}>
              {item.name}: {
                item.name.includes('Cost')
                  ? formatCurrency(item.value)
                  : item.name.includes('Hours')
                  ? `${item.value.toFixed(1)} hrs`
                  : item.name.includes('Time')
                  ? `${item.value.toFixed(1)} min`
                  : item.value.toLocaleString()
              }
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (view === 'monthly') {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Production</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={productionData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="month"
                stroke="#6B7280"
                fontSize={12}
                tickLine={false}
              />
              <YAxis
                yAxisId="left"
                stroke="#6B7280"
                fontSize={12}
                tickLine={false}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#6B7280"
                fontSize={12}
                tickLine={false}
                tickFormatter={(value) => `${value}h`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar
                yAxisId="left"
                dataKey="screens_produced"
                name="Screens"
                fill="#3B82F6"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                yAxisId="right"
                dataKey="avg_labor_hours"
                name="Avg Labor Hours"
                fill="#10B981"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  // Workstation view
  const sortedWorkstationData = [...(workstationData || [])].sort(
    (a, b) => b.avg_time_per_screen - a.avg_time_per_screen
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Avg Time by Workstation (minutes)</h3>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={sortedWorkstationData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              type="number"
              stroke="#6B7280"
              fontSize={12}
              tickLine={false}
              tickFormatter={(value) => `${value} min`}
            />
            <YAxis
              type="category"
              dataKey="workstation_name"
              stroke="#6B7280"
              fontSize={12}
              tickLine={false}
              width={120}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="avg_time_per_screen"
              name="Avg Time"
              fill="#8B5CF6"
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default ProductionChart;
